import { type DataTableProps, type TableColumn } from "@/types";
import { PaginationComponent } from "../dashboard/PaginationComponent";
import { cn } from "@/utils/cn";

/**
 * A reusable DataTable component that can display any type of data
 * 
 * @example
 * // Basic usage with custom rendering
 * const columns: TableColumn<User>[] = [
 *   { key: 'name', header: 'Full Name' },
 *   { 
 *     key: 'email', 
 *     header: 'Email',
 *     render: (value) => <a href={`mailto:${value}`}>{value}</a>
 *   },
 *   { 
 *     key: 'status', 
 *     header: 'Status', 
 *     align: 'center',
 *     render: (value) => <Badge variant={value}>{value}</Badge>
 *   }
 * ];
 * 
 * <DataTable 
 *   data={users} 
 *   columns={columns}
 *   showPagination={true}
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 * />
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function DataTable<T extends Record<string, any>>({
    data,
    columns,
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    showPagination = false,
    className,
    emptyMessage = "No data available",
    onRowClick,
}: DataTableProps<T>) {

    const getCellValue = (record: T, column: TableColumn<T>) => {
        const value = record[column.key];
        if (column.render) {
            const index = data.indexOf(record);
            return column.render(value, record, index);
        }
        return value;
    };

    const getTextAlign = (align: TableColumn<T>['align']) => {
        switch (align) {
            case 'center':
                return 'text-center';
            case 'right':
                return 'text-right';
            default:
                return 'text-left';
        }
    };

    if (data.length === 0) {
        return (
            <div className={cn("w-full", className)}>
                <div className="flex items-center justify-center py-12">
                    <p className="font-clash-display text-medium-grey text-sm font-medium">
                        {emptyMessage}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={cn("w-full", className)}>
            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-off-white">
                        <tr className="border-b border-stroke">
                            {columns.map((column, index) => (
                                <th
                                    key={String(column.key)}
                                    className={cn(
                                        "py-4 px-4 font-clash-display text-medium-grey text-xs font-medium",
                                        getTextAlign(column.align),
                                        index < columns.length - 1 && "border-r border-stroke"
                                    )}
                                    style={{ width: column.width }}
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((record, recordIndex) => (
                            <tr
                                key={recordIndex}
                                role={onRowClick ? "button" : undefined}
                                tabIndex={onRowClick ? 0 : undefined}
                                onClick={onRowClick ? () => onRowClick(record, recordIndex) : undefined}
                                onKeyDown={onRowClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(record, recordIndex); } } : undefined}
                                className={cn(
                                    "border-b border-stroke transition-colors hover:bg-off-white/50",
                                    recordIndex % 2 === 1 ? "bg-off-white/30" : "bg-white",
                                    onRowClick && "cursor-pointer"
                                )}
                            >
                                {columns.map((column, columnIndex) => (
                                    <td
                                        key={String(column.key)}
                                        className={cn(
                                            "py-4 px-4 font-clash-display text-sm",
                                            getTextAlign(column.align),
                                            columnIndex < columns.length - 1 && "border-r border-stroke"
                                        )}
                                    >
                                        {getCellValue(record, column)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {showPagination && onPageChange && totalPages > 1 && (
                <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
}
